<template>
  <div class="content-main users-page">
    <BaseModal v-model="showModal" @close="closeModal">
      <div class="p-6" style="min-width: 400px">
        <h3 class="text-lg font-semibold mb-4">
          {{ editingUser ? "Edit User" : "New User" }}
        </h3>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Name</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full border rounded px-3 py-2"
            placeholder="Full name"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Username</label>
          <input
            v-model="form.username"
            type="text"
            class="w-full border rounded px-3 py-2"
            placeholder="Username"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Email</label>
          <input
            v-model="form.email"
            type="email"
            class="w-full border rounded px-3 py-2"
            placeholder="Email"
          />
        </div>
        <div class="mb-3" v-if="!editingUser">
          <label class="block text-sm font-medium mb-1">Password</label>
          <input
            v-model="form.password"
            type="password"
            class="w-full border rounded px-3 py-2"
            placeholder="Password"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Role</label>
          <select v-model="form.role" class="w-full border rounded px-3 py-2">
            <option v-for="r in roleOptions" :key="r" :value="r">
              {{ r }}
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-2"
            >Special Permissions</label
          >
          <div class="grid grid-cols-2 gap-1">
            <label
              v-for="perm in availablePermissions"
              :key="perm.value"
              class="flex items-center gap-2 text-sm py-1"
            >
              <input
                type="checkbox"
                :value="perm.value"
                v-model="form.special_permissions"
              />
              {{ perm.label }}
            </label>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded hover:bg-gray-100"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            @click="saveUser"
            :disabled="saving"
          >
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <div class="users-header">
      <h1 class="users-title">Users</h1>
      <button class="btn-primary" @click="openCreate">+ New User</button>
    </div>

    <div class="users-filters">
      <input
        class="filter-input"
        type="text"
        placeholder="Filter users..."
        v-model="search"
      />
      <select class="filter-select" v-model="roleFilter">
        <option value="">All roles</option>
        <option v-for="r in roleOptions" :key="r" :value="r">{{ r }}</option>
      </select>
      <label class="inactive-toggle">
        <input type="checkbox" v-model="showInactive" />
        <span>Show inactive</span>
      </label>
      <span class="user-count">{{ filteredUsers.length }} users</span>
    </div>

    <div class="table-wrap">
      <div v-if="loading" class="empty-state">Loading...</div>
      <table v-else class="users-table">
        <thead>
          <tr>
            <th @click="setSort('role')" class="sortable">
              Role <span class="sort-ind">{{ sortIndicator("role") }}</span>
            </th>
            <th @click="setSort('name')" class="sortable">
              Name <span class="sort-ind">{{ sortIndicator("name") }}</span>
            </th>
            <th @click="setSort('username')" class="sortable">
              Username
              <span class="sort-ind">{{ sortIndicator("username") }}</span>
            </th>
            <th>Email</th>
            <th>Permissions</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in filteredUsers"
            :key="user.uuid"
            :class="{ 'row-inactive': !isEnabled(user) }"
          >
            <td>
              <span class="role-badge" :class="roleClass(user.role)">
                {{ user.role }}
              </span>
            </td>
            <td class="font-medium">{{ user.name }}</td>
            <td>{{ user.username }}</td>
            <td>{{ user.email }}</td>
            <td>
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="perm in permsOf(user)"
                  :key="perm"
                  class="perm-badge"
                >
                  {{ perm }}
                </span>
                <span v-if="!permsOf(user).length" class="text-xs text-gray-400"
                  >—</span
                >
              </div>
            </td>
            <td>
              <span
                class="status-badge"
                :class="
                  isEnabled(user)
                    ? 'status-badge--active'
                    : 'status-badge--inactive'
                "
                @click="toggleUserEnabled(user)"
                title="Click to toggle"
              >
                {{ isEnabled(user) ? "Active" : "Disabled" }}
              </span>
            </td>
            <td>
              <button class="link-blue" @click="openEdit(user)">Edit</button>
              <button class="link-red ml-2" @click="confirmDelete(user)">
                Delete
              </button>
            </td>
          </tr>
          <tr v-if="!filteredUsers.length">
            <td colspan="7" class="empty-state">No users match your filters</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  toggleEnabled
} from "@/api/UsersService.js";

const ROLE_ORDER = ["superAdmin", "admin", "user", "driver"];

export default {
  name: "UsersPage",
  data() {
    return {
      users: [],
      loading: false,
      saving: false,
      showModal: false,
      editingUser: null,
      search: "",
      roleFilter: "",
      showInactive: false,
      sortKey: "role",
      sortDir: "asc",
      roleOptions: ["user", "admin", "superAdmin", "driver"],
      form: {
        name: "",
        username: "",
        email: "",
        password: "",
        role: "user",
        special_permissions: []
      },
      availablePermissions: [
        { value: "can_edit_carriers", label: "Edit Carriers" },
        { value: "can_edit_users", label: "Edit Users" },
        { value: "can_manage_warehouse", label: "Manage Warehouse" },
        { value: "can_see_orders_mia", label: "See Orders MIA" },
        { value: "can_see_orders_imp", label: "See Orders IMP" }
      ]
    };
  },
  computed: {
    filteredUsers() {
      const q = this.search.trim().toLowerCase();
      const filtered = this.users.filter(u => {
        if (!this.showInactive && !this.isEnabled(u)) return false;
        if (this.roleFilter && u.role !== this.roleFilter) return false;
        if (!q) return true;
        return (
          (u.name || "").toLowerCase().includes(q) ||
          (u.username || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
        );
      });
      const dir = this.sortDir === "asc" ? 1 : -1;
      const valOf = u => {
        if (this.sortKey === "role") {
          const idx = ROLE_ORDER.indexOf(u.role);
          return idx === -1 ? ROLE_ORDER.length : idx;
        }
        return (u[this.sortKey] || "").toString().toLowerCase();
      };
      return filtered.slice().sort((a, b) => {
        const av = valOf(a);
        const bv = valOf(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
  },
  mounted() {
    this.loadUsers();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const res = await getUsers({ page: 1, per_page: 1000 });
        this.users = res.items || res.data || res;
      } finally {
        this.loading = false;
      }
    },
    isEnabled(user) {
      return user.enabled === true || user.enabled == "1" || user.enabled === 1;
    },
    permsOf(user) {
      return (user.special_permissions || "").split(",").filter(Boolean);
    },
    roleClass(role) {
      return {
        "role-badge--super": role === "superAdmin",
        "role-badge--admin": role === "admin",
        "role-badge--user": role === "user",
        "role-badge--driver": role === "driver"
      };
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    sortIndicator(key) {
      if (this.sortKey !== key) return "⇅";
      return this.sortDir === "asc" ? "▲" : "▼";
    },
    openCreate() {
      this.editingUser = null;
      this.form = {
        name: "",
        username: "",
        email: "",
        password: "",
        role: "user",
        special_permissions: []
      };
      this.showModal = true;
    },
    openEdit(user) {
      this.editingUser = user;
      this.form = {
        name: user.name || "",
        username: user.username || "",
        email: user.email || "",
        password: "",
        role: user.role || "user",
        special_permissions: this.permsOf(user)
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingUser = null;
    },
    async saveUser() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        payload.special_permissions = payload.special_permissions.join(",");
        if (this.editingUser) {
          if (!payload.password) delete payload.password;
          await updateUser(this.editingUser.uuid, payload);
        } else {
          await createUser(payload);
        }
        this.closeModal();
        this.loadUsers();
      } catch (e) {
        alert(e?.response?.data?.message || "Error saving user");
      } finally {
        this.saving = false;
      }
    },
    async toggleUserEnabled(user) {
      try {
        await toggleEnabled(user.uuid);
        this.loadUsers();
      } catch (e) {
        alert("Error toggling user status");
      }
    },
    confirmDelete(user) {
      if (confirm(`Delete user "${user.name}"? This action is irreversible.`)) {
        this.doDelete(user);
      }
    },
    async doDelete(user) {
      try {
        await deleteUser(user.uuid);
        this.loadUsers();
      } catch (e) {
        alert("Error deleting user");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.users-page {
  width: 100%;
  max-width: none;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.users-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.users-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  &:hover {
    background: #1d4ed8;
  }
}

.users-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
}
.filter-input {
  flex: 1;
  min-width: 220px;
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.filter-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #6366f1;
  }
}
.inactive-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.825rem;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
}
.user-count {
  font-size: 0.8rem;
  color: #9ca3af;
  margin-left: auto;
}

.table-wrap {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: auto;
}
.users-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th,
  td {
    text-align: left;
    padding: 0.625rem 0.875rem;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }
  tbody tr:hover {
    background: #f9fafb;
  }
  tbody tr.row-inactive {
    opacity: 0.55;
  }
}
.sortable {
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #f3f4f6 !important;
  }
}
.sort-ind {
  margin-left: 0.25rem;
  font-size: 0.65rem;
  color: #9ca3af;
}

.role-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: #e0e7ff;
  color: #4338ca;
  &--super {
    background: #fee2e2;
    color: #b91c1c;
  }
  &--admin {
    background: #dbeafe;
    color: #1d4ed8;
  }
  &--user {
    background: #f3f4f6;
    color: #4b5563;
  }
  &--driver {
    background: #fef3c7;
    color: #b45309;
  }
}
.perm-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #f5f3ff;
  color: #6d28d9;
}
.status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  cursor: pointer;
  &--active {
    background: #dcfce7;
    color: #15803d;
  }
  &--inactive {
    background: #fee2e2;
    color: #b91c1c;
  }
}

.link-blue {
  color: #2563eb;
  font-size: 0.8rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.link-red {
  color: #dc2626;
  font-size: 0.8rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.empty-state {
  padding: 2.5rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}
</style>
